import { useMemo, type ReactNode } from 'react';
import { Typography } from '@/components/ui/typography';
import type { DisplayService } from '../../../../types/order.types';
import { ServiceCard } from './ServiceCard';

interface ServiceCardGridProps {
  services: DisplayService[];
  /**
   * Which step of the service slider this grid is: the packages (plus
   * `belowPrimaries`), or the selected package's add-ons (DV-3204 moved them
   * onto their own step).
   */
  view: 'packages' | 'add-ons';
  onSelect: (slug: string) => void;
  onDeselect: (slug: string) => void;
  onQuantityChange: (slug: string, qty: number) => void;
  /** Does this package come with a free floor plan? Badges its card (DV-3204). */
  offersFreeFloorPlan?: (service: DisplayService) => boolean;
  /** Rendered under the packages: the floor plan tier choice. */
  belowPrimaries?: ReactNode;
}

// Each ServiceCard spans five rows of this grid and lays its sections out on
// them through subgrid, which is what lines the sections up across a row.
const CARD_GRID_CLASSES = 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4';

export function ServiceCardGrid({
  services,
  view,
  onSelect,
  onDeselect,
  onQuantityChange,
  offersFreeFloorPlan,
  belowPrimaries,
}: ServiceCardGridProps) {
  // Keep primaries in their original API sort order — no re-ordering on select.
  // Only one primary may be active at a time: once a primary is selected, the
  // others are hidden (mirrors pre-migration NewServiceUI's checkSelectedService
  // gate), leaving just the selected primary + its add-ons.
  const primaries = useMemo(() => {
    const allPrimaries = services.filter((s) => s.type === 'primary');
    const selectedPrimaries = allPrimaries.filter((s) => s.isSelected);
    return selectedPrimaries.length > 0 ? selectedPrimaries : allPrimaries;
  }, [services]);
  const addOns = useMemo(
    () => services.filter((s) => s.type === 'secondary'),
    [services],
  );

  const allSelected = services.filter((s) => s.isSelected);
  const minSelectedPriority = allSelected.length
    ? Math.min(
        ...allSelected.map((s) => Number(s.primaryServicePriority ?? 999)),
      )
    : null;

  if (view === 'add-ons') {
    if (addOns.length === 0) {
      return (
        <Typography as="p" variant="body" className="text-muted-foreground">
          No additional services are available for this package.
        </Typography>
      );
    }
    return (
      <div className={CARD_GRID_CLASSES}>
        {addOns.map((svc) => (
          <ServiceCard
            key={svc.uuid}
            service={svc}
            minSelectedPriority={minSelectedPriority}
            onSelect={onSelect}
            onDeselect={onDeselect}
            onQuantityChange={onQuantityChange}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Primary services — responsive grid */}
      <div className={CARD_GRID_CLASSES}>
        {primaries.map((svc) => (
          <ServiceCard
            key={svc.uuid}
            service={svc}
            minSelectedPriority={minSelectedPriority}
            onSelect={onSelect}
            onDeselect={onDeselect}
            onQuantityChange={onQuantityChange}
            offersFreeFloorPlan={offersFreeFloorPlan?.(svc) ?? false}
          />
        ))}
      </div>

      {belowPrimaries}
    </div>
  );
}
